import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  MapPin,
  Phone,
  Mail,
} from 'lucide-react'
import { settingsApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import { parseSettingsResponse, getLocalizedSetting } from '../../utils/settings'
import { DEFAULT_CONTACT } from '../../constants/contact'
import { useContactHref } from '../../hooks/useContactHref'
import { PageSlugs } from '../../constants/pageSlugs'
import { SOCIAL_LINKS, SOCIAL_CHANNELS } from '../../constants/social'

const DEFAULT_SETTINGS = {
  ...DEFAULT_CONTACT,
  address_en: DEFAULT_CONTACT.address_en,
  address_ar: DEFAULT_CONTACT.address_ar,
  facebook: SOCIAL_LINKS.facebook,
  instagram: SOCIAL_LINKS.instagram,
  twitter: SOCIAL_LINKS.twitter,
  youtube: SOCIAL_LINKS.youtube,
  whatsapp: SOCIAL_LINKS.whatsapp,
}

export default function Footer() {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const contactHref = useContactHref()
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)

  useEffect(() => {
    settingsApi
      .get()
      .then(({ data }) => setSettings({ ...DEFAULT_SETTINGS, ...parseSettingsResponse(data, language) }))
      .catch(() => {})
  }, [language])

  const quickLinks = [
    { to: '/', label: t('nav.home') },
    { to: `/pages/${PageSlugs.leadership}`, label: t('nav.about') },
    { to: '/gallery', label: t('nav.gallery') },
    { to: '/news', label: t('nav.news') },
    { to: '/events', label: t('nav.events') },
    { to: contactHref, label: t('nav.contact') },
  ]

  const socialLinks = SOCIAL_CHANNELS.map(({ key, icon, label }) => ({
    icon,
    href:
      (key === 'facebook' && settings.facebook) ||
      (key === 'instagram' && settings.instagram) ||
      (key === 'youtube' && settings.youtube) ||
      (key === 'twitter' && settings.twitter) ||
      (key === 'whatsapp' && settings.whatsapp) ||
      SOCIAL_LINKS[key],
    label,
    key,
  })).filter((s) => s.href)

  return (
    <footer className="relative overflow-hidden bg-primary text-white">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -end-20 -top-20 h-80 w-80 rounded-full bg-accent blur-3xl" />
        <div className="absolute -bottom-20 -start-20 h-80 w-80 rounded-full bg-secondary blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/"
              onClick={() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
                document.documentElement.scrollTop = 0
                document.body.scrollTop = 0
              }}
              className="inline-block"
            >
              <img
                src="/logo/logo-white.png"
                alt={t('brand.name')}
                className="mb-4 h-14 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
            </Link>
            <h3 className="mb-2 text-xl font-bold">
              {getLocalizedSetting(settings, 'school_name', language) || t('brand.name')}
            </h3>
            <p className="text-sm leading-relaxed text-white/75">
              {t('footer.description')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-accent">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-white/75 transition-colors hover:text-accent"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-accent">
              {t('footer.contactInfo')}
            </h4>
            <ul className="space-y-3 text-sm text-white/75">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                <span>{getLocalizedSetting(settings, 'address', language)}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 flex-shrink-0 text-accent" />
                <a href={`tel:${settings.phone}`} className="hover:text-accent">
                  {settings.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 flex-shrink-0 text-accent" />
                <a href={`tel:${settings.mobile}`} className="hover:text-accent">
                  {settings.mobile}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 flex-shrink-0 text-accent" />
                <a href={`mailto:${settings.email}`} className="break-all hover:text-accent">
                  {settings.email}
                </a>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-accent">
              {t('footer.followUs')}
            </h4>
            <div className="flex gap-3">
              {socialLinks.map(({ icon: Icon, href, label, key }) => (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/75 transition-all hover:scale-110 hover:border-white/30 hover:bg-white/15 hover:text-white"
                  >
                    <Icon className="h-5 w-5 fill-current" />
                  </a>
                ))}
            </div>
          </motion.div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/15 pt-8 text-center sm:flex-row sm:text-start">
          <p className="text-sm text-white/60">
            © {new Date().getFullYear()} {t('brand.name')}. {t('footer.rights')}
          </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-white/60 sm:justify-end">
            <Link to={`/pages/${PageSlugs.privacy}`} className="hover:text-accent">
              {t('footer.privacy')}
            </Link>
            <Link to={`/pages/${PageSlugs.terms}`} className="hover:text-accent">
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
